import { getAccessToken, waitForAccessToken } from "./authToken";
import {
    buildLeadsListSearchBody,
    buildLeadsListSortBody,
    findLeadRowByCin,
    normalizeLeadsPage
} from "./leadsListApi";

/**
 * API stage root for member summary.
 * Prefer `VITE_MEMBER_SERVICES_BASE` (same pattern as `VITE_LEAD_SERVICES_BASE`).
 * POST `{base}/accounts`, GET `{base}/lcm-summary/{cin}`, GET `{base}/rn-summary/{cin}`,
 * POST `{base}/member-summary/{cin}/generate` with `{ "role": "lcm"|"rn" }`.
 */
export function getMemberServicesApiBase() {
    return String(import.meta.env.VITE_MEMBER_SERVICES_BASE || "")
        .trim()
        .replace(/\/$/, "");
}

/**
 * Full URL to POST /accounts for the Members Summary list.
 * Prefer `VITE_MEMBERS_LIST_URL`; else `${VITE_MEMBER_SERVICES_BASE}/accounts`.
 */
export function getMembersListUrl() {
    const explicit = String(import.meta.env.VITE_MEMBERS_LIST_URL || "").trim();
    if (explicit) {
        return explicit;
    }
    const base = getMemberServicesApiBase();
    return base ? `${base}/accounts` : "";
}

export function shouldUseMemberSummaryRemote() {
    return !!getMemberServicesApiBase();
}

/** GET {base}/filters — member list filter options. */
export function getMemberFiltersUrl() {
    const base = getMemberServicesApiBase();
    return base ? `${base}/filters` : "";
}

/** In-memory cache; cleared when leaving Members Summary list (see `invalidateMemberFiltersCache`). */
let memberFiltersPromise = null;

export function invalidateMemberFiltersCache() {
    memberFiltersPromise = null;
}

/**
 * @param {unknown} data
 * @returns {{ clinicalScore: string[], eligibility: string[], memberStatus: string[] }}
 */
export function normalizeMemberFiltersResponse(data) {
    const payload = data && typeof data === "object" ? data : {};
    const toStrings = (value) =>
        Array.isArray(value) ? value.map((item) => String(item || "").trim()).filter(Boolean) : [];
    return {
        clinicalScore: toStrings(payload.lead_score),
        eligibility: toStrings(payload.waystar_authorization),
        memberStatus: toStrings(payload.member_status)
    };
}

/**
 * Map UI filter groups to POST /accounts `filters` object.
 * @param {{ clinicalScore?: string[], eligibility?: string[], memberStatus?: string[] } | null | undefined} uiFilters
 */
export function buildMembersListFiltersBody(uiFilters) {
    const src = uiFilters || {};
    return {
        lead_score: Array.isArray(src.clinicalScore) ? [...src.clinicalScore] : [],
        waystar_authorization: Array.isArray(src.eligibility) ? [...src.eligibility] : [],
        member_status: Array.isArray(src.memberStatus) ? [...src.memberStatus] : []
    };
}

/**
 * Build JSON body for POST /accounts (members list).
 * @param {object} [options]
 */
export function buildMembersListRequestBody(options = {}) {
    const pageNum = Number(options.page);
    const pageSizeNum = Number(options.pageSize ?? options.page_size);
    const pageSize =
        Number.isFinite(pageSizeNum) && pageSizeNum > 0 ? Math.floor(pageSizeNum) : 50;
    return {
        filters: buildMembersListFiltersBody(options.filters),
        search: buildLeadsListSearchBody(options),
        sort: buildLeadsListSortBody(options.sortKey, options.sortDirection),
        page: Number.isFinite(pageNum) && pageNum >= 1 ? Math.floor(pageNum) : 1,
        page_size: pageSize
    };
}

/**
 * GET {base}/filters with Bearer auth.
 * @returns {Promise<{ clinicalScore: string[], eligibility: string[], memberStatus: string[] }>}
 */
export async function fetchMemberFilters() {
    if (memberFiltersPromise) {
        return memberFiltersPromise;
    }

    memberFiltersPromise = (async () => {
        const url = getMemberFiltersUrl();
        if (!url) {
            throw new Error(
                "Member filters API is not configured (set VITE_MEMBER_SERVICES_BASE)."
            );
        }

        const data = await authorizedMemberJson(url, { action: "load member filters" });
        return normalizeMemberFiltersResponse(data);
    })();

    try {
        return await memberFiltersPromise;
    } catch (err) {
        memberFiltersPromise = null;
        throw err;
    }
}

/** @param {unknown} raw */
export function normalizeMemberSummaryKind(raw) {
    return String(raw || "").trim().toLowerCase() === "rn" ? "rn" : "lcm";
}

/**
 * Nested summary object from an LCM/RN (or legacy lead) response.
 * Envelope-only / “not generated” payloads return `{}` so the UI can show
 * the Not Yet Generated empty state (same as lead summary).
 * @param {unknown} apiData
 * @param {"lcm"|"rn"} kind
 */
export function pickMemberSummaryPayload(apiData, kind) {
    if (!apiData || typeof apiData !== "object" || Array.isArray(apiData)) {
        return {};
    }
    const data = apiData;
    const kindKey = kind === "rn" ? "rn_summary" : "lcm_summary";
    const nested = [data[kindKey], data.lead_summary, data.member_summary, data.summary].find(
        (value) => value && typeof value === "object" && !Array.isArray(value)
    );
    if (nested) {
        return nested;
    }
    if (isMemberSummaryEnvelopeOnly(data)) {
        return {};
    }
    return data;
}

/** True when the response is metadata only (no SBAR / Q&A summary body). */
function isMemberSummaryEnvelopeOnly(data) {
    if (data.summary_available === false) {
        return true;
    }
    const message = String(data.message || data.detail || data.error || "")
        .trim()
        .toLowerCase();
    if (
        message &&
        (message.includes("summary not generated") ||
            message.includes("not generated") ||
            message.includes("not yet queued") ||
            message.includes("not available"))
    ) {
        return true;
    }
    const envelopeKeys = new Set([
        "cin",
        "cin_no",
        "summary_available",
        "message",
        "detail",
        "error",
        "status",
        "last_updated",
        "summary_date",
        "member_details"
    ]);
    return Object.keys(data).every((key) => envelopeKeys.has(key));
}

/** Rows from recent Members Summary POST /accounts responses in this session. */
let membersListSessionCache = [];

function mergeMembersIntoSessionCache(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return;
    }
    const byCin = new Map(
        membersListSessionCache.map((r) => [String(r?.cin || "").trim().toUpperCase(), r])
    );
    for (const row of items) {
        const key = String(row?.cin || "").trim().toUpperCase();
        if (key) {
            byCin.set(key, row);
        }
    }
    membersListSessionCache = Array.from(byCin.values());
}

/**
 * @param {string} cin
 * @returns {Record<string, unknown>|null}
 */
export function getCachedMemberRowByCin(cin) {
    return findLeadRowByCin(membersListSessionCache, cin);
}

/**
 * POST members `/accounts` with Bearer auth (same body shape as lead list).
 * @param {Parameters<typeof buildLeadsListRequestBody>[0]} [options]
 */
export async function fetchMembersList(options = {}) {
    const listUrl = getMembersListUrl();
    if (!listUrl) {
        throw new Error(
            "Members list API is not configured (set VITE_MEMBER_SERVICES_BASE or VITE_MEMBERS_LIST_URL)."
        );
    }

    await waitForAccessToken(5000);
    const token = getAccessToken();
    if (!token) {
        throw new Error("You must be signed in to view members.");
    }

    const body = buildMembersListRequestBody(options);

    const res = await fetch(listUrl, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });

    if (!res.ok) {
        let detail = "";
        try {
            const errBody = await res.json();
            detail = errBody.message || errBody.error || errBody.detail || "";
        } catch {
            try {
                detail = await res.text();
            } catch {
                detail = "";
            }
        }
        const msg = detail ? `${detail}` : `Request failed (${res.status})`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }

    const rawText = await res.text();
    let data;
    try {
        data = rawText ? JSON.parse(rawText) : null;
    } catch {
        throw new Error("Members API returned invalid JSON.");
    }

    const page = normalizeLeadsPage(data);
    if (page.items.length > 0) {
        mergeMembersIntoSessionCache(page.items);
    }
    return page;
}

function memberSummaryPath(kind) {
    return normalizeMemberSummaryKind(kind) === "rn" ? "rn-summary" : "lcm-summary";
}

async function authorizedMemberJson(
    url,
    { method = "GET", action = "view member summaries", body } = {}
) {
    await waitForAccessToken(5000);
    const token = getAccessToken();
    if (!token) {
        throw new Error(`You must be signed in to ${action}.`);
    }

    const headers = {
        Accept: "application/json",
        Authorization: `Bearer ${token}`
    };
    const hasBody = body !== undefined;
    if (hasBody) {
        headers["Content-Type"] = "application/json";
    }

    const res = await fetch(url, {
        method,
        headers,
        body: hasBody ? JSON.stringify(body) : undefined
    });

    if (!res.ok) {
        let detail = "";
        try {
            const errBody = await res.json();
            detail = errBody.message || errBody.error || errBody.detail || "";
        } catch {
            try {
                detail = await res.text();
            } catch {
                detail = "";
            }
        }
        const msg = detail ? `${detail}` : `Request failed (${res.status})`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }

    return res.json();
}

/**
 * GET {base}/lcm-summary/{cin} or {base}/rn-summary/{cin} with Okta ID token (Bearer).
 * @param {string} cin
 * @param {"lcm"|"rn"} [kind]
 */
export async function fetchMemberSummaryByCin(cin, kind = "lcm") {
    const base = getMemberServicesApiBase();
    if (!base) {
        throw new Error(
            "Member summary API is not configured (set VITE_MEMBER_SERVICES_BASE)."
        );
    }
    const id = String(cin || "").trim();
    if (!id) {
        throw new Error("CIN is required.");
    }

    const url = `${base}/${memberSummaryPath(kind)}/${encodeURIComponent(id)}`;
    return authorizedMemberJson(url, { action: "view member summaries" });
}

/**
 * POST {base}/member-summary/{cin}/generate with body `{ "role": "lcm" | "rn" }`.
 * Used for refresh / generate on Member Summary only.
 * @param {string} cin
 * @param {"lcm"|"rn"} [kind]
 */
export async function generateMemberSummaryByCin(cin, kind = "lcm") {
    const base = getMemberServicesApiBase();
    if (!base) {
        throw new Error(
            "Member summary API is not configured (set VITE_MEMBER_SERVICES_BASE)."
        );
    }
    const id = String(cin || "").trim();
    if (!id) {
        throw new Error("CIN is required.");
    }

    const role = normalizeMemberSummaryKind(kind);
    const url = `${base}/member-summary/${encodeURIComponent(id)}/generate`;
    return authorizedMemberJson(url, {
        method: "POST",
        action: "refresh member summaries",
        body: { role }
    });
}

// Terminal on-demand statuses (backend vocabulary) and their user-facing messages.
const MEMBER_TERMINAL_STATUSES = new Set([
    "generated", "regenerated", "repaired", "unchanged",
    "failed_patient_not_found", "failed_bedrock", "failed_docref_write", "failed_pipeline"
]);
const MEMBER_STATUS_MESSAGE = {
    generated: "New summary generated",
    regenerated: "Summary refreshed",
    repaired: "Summary restored",
    unchanged: "No change — existing summary kept",
    failed_patient_not_found: "Patient not found",
    failed_bedrock: "Could not generate summary",
    failed_docref_write: "Summary generated but not stored",
    failed_pipeline: "Failed"
};

/**
 * Async generate + poll. The generate endpoint returns 202 { status:"generating", request_id }
 * immediately; the worker runs ~1-1.5 min. This POSTs, then polls the GET summary's `on_demand`
 * block until it reports THIS run's request_id with a terminal status — so the caller can hold a
 * spinner the whole time and show the REAL outcome (refreshed vs no-change) instead of guessing.
 *
 * @returns {Promise<{status:string, message:string, apiData:any, failed:boolean}>}
 */
export async function generateAndPollMemberSummary(cin, kind = "lcm", opts = {}) {
    const intervalMs = opts.intervalMs ?? 10000;
    // Generation time is NOT fixed: ~3s (unchanged) to ~1-1.5 min (regenerate), longer for a
    // whale member. Poll generously (the backend worker Lambda caps at 600s) so the spinner
    // waits for the real result instead of giving up early.
    const timeoutMs = opts.timeoutMs ?? 300000;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const finalize = (status, message, apiData) => ({
        status,
        message: message || MEMBER_STATUS_MESSAGE[status] || "Done",
        apiData,
        failed: String(status || "").startsWith("failed")
    });

    const started = await generateMemberSummaryByCin(cin, kind);
    const requestId = String(started?.request_id ?? "").trim();

    // Fallback: a synchronous backend (no request_id) returns the terminal status directly.
    if (!requestId) {
        const status = String(started?.status ?? "").trim().toLowerCase();
        const apiData = await fetchMemberSummaryByCin(cin, kind);
        return finalize(status, started?.message, apiData);
    }

    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        await sleep(intervalMs);
        let apiData;
        try {
            apiData = await fetchMemberSummaryByCin(cin, kind);
        } catch (_e) {
            continue; // transient poll error — keep waiting
        }
        const od = apiData?.on_demand;
        const status = String(od?.status ?? "").trim().toLowerCase();
        if (od && String(od.request_id ?? "").trim() === requestId && MEMBER_TERMINAL_STATUSES.has(status)) {
            return finalize(status, od.message, apiData);
        }
    }
    const err = new Error("Still processing — please try again shortly.");
    err.code = "timeout";
    throw err;
}
